import { SUBDOMAIN_PATTERN } from '@/lib/constants';
import type { OAuthAuthorizeRequest } from '@/lib/oauthAuthorize';
import type { Translation } from './i18n';
import { getPlatformHostsUrl, getPlatformValidationUrl } from './platformConfig';
import type { KnownPlatform, PlatformResponse } from './platformTypes';

export type PlatformValidationResult =
	| { error: string; platform?: never; response?: never }
	| { error?: never; platform: KnownPlatform; response: PlatformResponse };

export async function fetchSupportedPlatformHosts(): Promise<string[]> {
	const response = await fetch(getPlatformHostsUrl());
	if (!response.ok) throw new Error('Unable to fetch supported platform hosts.');

	const hosts: unknown = await response.json();
	if (!Array.isArray(hosts) || hosts.some((host) => typeof host !== 'string' || !host))
		throw new Error('Invalid supported platform hosts response.');

	return hosts;
}

export async function validatePlatform(
	subdomain: string,
	translation: Translation,
	oauth?: OAuthAuthorizeRequest,
): Promise<PlatformValidationResult> {
	const normalized = subdomain.trim().toLowerCase();
	if (!SUBDOMAIN_PATTERN.test(normalized)) return { error: translation.invalid };

	try {
		const response = await fetch(getPlatformValidationUrl(), {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ subdomain: normalized, ...(oauth ? { oauth } : {}) }),
		});
		const data = (await response.json()) as PlatformResponse & { error?: string };
		if (!response.ok)
			return {
				error:
					data.error === 'platform_unavailable'
						? translation.unavailable
						: data.error === 'platform_invalid'
							? translation.invalid
							: translation.unknown,
			};

		if (oauth && typeof data.authorization_url !== 'string') return { error: translation.unknown };

		return {
			platform: {
				subdomain: normalized,
				host: data.host,
				name: data.branding.name,
				logo: data.branding.logo,
				primaryColor: data.branding.primary_color,
			},
			response: data,
		};
	} catch {
		return { error: translation.unknown };
	}
}
